import { z } from 'zod';
/** Settings/admin (v2.0, Phase 20). Stages/statuses reuse `pipelineItemSchema`. */
/**
 * Saved Status Action (V38). A named, reusable action that, when fired on a
 * contact, advances the per-pool `ContactStatus` ladder to `toContactStatusId`
 * (the "New Status"), bumps a Global/Unique counter, logs a touch, and records
 * the From→To transition. The action's pool is DERIVED from its target status —
 * there is no category field on the input; `channel`/`outcome` default server-
 * side for the logged touch.
 *
 * Fields mirror the mockup: `fromContactStatusIds` (Current Status — legal FROM
 * rungs), `showOnContactStatusIds` (menu visibility, display-only), the
 * contact-type / dashboard-view / visible-to filters, the `global` scope flag,
 * and an optional form-fill reminder. `contactTypeIds` may contain `"__all__"`.
 * `visibleToIds` entries are prefixed `u:<userId>` or `r:<role>`.
 */
export const REMINDER_FORM_DOMAINS = ['forms', 'formshub'];
export const CONTACT_TYPE_ALL = '__all__';
export const contactActionCreateSchema = z
    .object({
    label: z.string().min(1).max(80),
    toContactStatusId: z.string().uuid({ message: 'Select a New Status' }),
    fromContactStatusIds: z.array(z.string().uuid()).default([]),
    showOnContactStatusIds: z.array(z.string().uuid()).default([]),
    contactTypeIds: z.array(z.string().min(1)).default([]),
    dashboardViewIds: z.array(z.string().min(1)).default([]),
    visibleToIds: z.array(z.string().min(1)).default([]),
    global: z.boolean().default(true),
    reminderFormId: z.string().uuid().nullable().optional(),
    reminderFormNote: z.string().max(500).nullable().optional(),
    reminderFormUrl: z.string().max(500).nullable().optional(),
    reminderFormDomain: z.enum(REMINDER_FORM_DOMAINS).default('forms'),
    position: z.number().int().min(0).optional(),
})
    // Current & New must differ — an action that "moves" a contact to where it
    // already is (every FROM equals TO) is a no-op transition (mockup saveAction).
    .refine((v) => v.fromContactStatusIds.length === 0 ||
    !v.fromContactStatusIds.every((id) => id === v.toContactStatusId), { message: 'Current and New status must differ', path: ['toContactStatusId'] });
/** Drag-reorder the saved actions within a pool — ordered ids set position 0..n. */
export const contactActionReorderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
export const SETTINGS_ROLES = [
    'admin',
    'manager',
    'clinician',
    'supervisor',
    'front_desk',
];
export const roleAssignSchema = z.object({ role: z.enum(SETTINGS_ROLES) });
/**
 * Permission-key catalog (Phase 20b, ADR-0008). Layered OVER the role tiers.
 * Effective set = role defaults ∪ per-user grants − per-user revokes.
 */
export const PERMISSION_CATALOG = [
    { key: 'view_phi', domain: 'phi', label: 'View PHI fields on client records' },
    { key: 'edit_phi', domain: 'phi', label: 'Edit PHI fields on client records' },
    { key: 'export_phi', domain: 'phi', label: 'Include PHI in exports (otherwise redacted)' },
    { key: 'view_others_contacts', domain: 'contacts', label: 'View others’ contacts' },
    { key: 'archive_contact', domain: 'contacts', label: 'Archive contacts' },
    { key: 'note_log', domain: 'notes', label: 'Add notes' },
    { key: 'task_delete', domain: 'notes', label: 'Delete tasks' },
    { key: 'manage_users', domain: 'admin', label: 'Manage users & roles' },
    { key: 'manage_stages', domain: 'admin', label: 'Edit stages' },
    { key: 'manage_statuses', domain: 'admin', label: 'Edit status types' },
    { key: 'view_audit_log', domain: 'admin', label: 'View the audit log' },
    { key: 'billing_manage', domain: 'admin', label: 'Manage billing & payouts' },
];
/** Default key set per role. PHI keys are OFF for front_desk (ADR-0008 fail-safe). */
export const ROLE_DEFAULTS = {
    admin: PERMISSION_CATALOG.map((p) => p.key),
    manager: [
        'view_phi',
        'edit_phi',
        'view_others_contacts',
        'archive_contact',
        'note_log',
        'task_delete',
        'manage_users',
        'manage_stages',
        'manage_statuses',
        'view_audit_log',
        'billing_manage',
    ],
    clinician: ['view_phi', 'edit_phi', 'note_log', 'task_delete'],
    supervisor: ['view_phi', 'edit_phi', 'note_log', 'task_delete', 'view_audit_log'],
    front_desk: ['view_others_contacts', 'archive_contact'],
};
export const PIPELINE_KINDS = ['stages', 'statuses'];
export function isPipelineKind(k) {
    return PIPELINE_KINDS.includes(k);
}
//# sourceMappingURL=settings.js.map